import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import {
  ArrowUpRight, Building2, ShieldCheck, Globe2, Layers, Wallet,
  Sparkles, Check, Zap, MessageCircle, Paperclip, X, Send, Menu,
  ChevronRight, Star,
} from "lucide-react";

/* ─── Design tokens ─────────────────────────────────────────────────────────── */
const C = {
  blue: "#3B82F6",
  purple: "#8B5CF6",
  bg: "#f4f6ff",
  card: "#ffffff",
  text: "#0d0d1a",
  muted: "#4a4a6a",
  border: "#e2e8f8",
  gradFrom: "#3B82F6",
  gradTo: "#8B5CF6",
};

const grad = `linear-gradient(135deg, ${C.gradFrom} 0%, ${C.gradTo} 100%)`;

/* ─── Translations ───────────────────────────────────────────────────────────── */
const TRANSLATIONS = {
  fr: {
    nav: {
      product: "Produit", templates: "Templates", pricing: "Tarifs",
      domains: "Domaines", login: "Connexion", cta: "Lancer ma banque",
    },
    hero: {
      badge: "SaaS Multi-Tenant · Bank-in-a-Box",
      h1a: "Lancez votre", h1b: "banque en ligne", h1c: "en quelques minutes.",
      sub: "Une plateforme white-label complète pour les entrepreneurs et fintechs. Choisissez un template, personnalisez votre marque, connectez votre domaine — et vous êtes en ligne.",
      ctaPrimary: "Démarrer gratuitement", ctaSecondary: "Voir les tarifs",
      trust1: "4 templates premium", trust2: "DNS & SSL auto-configurés", trust3: "JWT + bcrypt sécurisé",
      balanceLabel: "Solde courant", balanceNote: "Aperçu — Template Tech Neon",
    },
    features: {
      label: "Plateforme",
      h2: "Tout ce qu'il faut pour devenir un opérateur bancaire.",
      sub: "Infrastructure, sécurité, UX — tout est déjà là. Vous apportez votre marque.",
      items: [
        { title: "Banques illimitées", text: "Un abonnement = une banque. Multipliez les projets, gardez le contrôle." },
        { title: "Templates premium", text: "4 designs distinctifs — du minimal suisse au neon fintech." },
        { title: "Wallet & cartes virtuelles", text: "Cartes virtuelles, transactions, soldes — déjà câblés." },
        { title: "Sous-domaine automatique", text: "Un sous-domaine est attribué dès la création. Achetez votre domaine custom depuis la plateforme." },
        { title: "Sécurité bancaire", text: "JWT, hash bcrypt, journalisation. Architecture multi-tenant isolée." },
        { title: "White-label total", text: "Logo, couleurs, devise, langue. Votre marque, votre banque." },
      ],
    },
    templates: {
      label: "Catalogue",
      h2: "Plus de 4 univers de banque.", h2b: "Une infinité de marques.",
      sub: "Chaque template est un système de design complet — du dashboard à la carte virtuelle.",
      cardH3: "Une expérience client premium.",
      cardSub: "Dashboard, virements, cartes, dépôts, crédits — tout est déjà conçu. Vous changez les couleurs et le logo, vos clients voient votre banque.",
      cardCta: "Voir la démo",
    },
    pricing: {
      label: "Tarifs",
      h2a: "Un pack. Une banque.", h2b: "Payé une seule fois.",
      sub: "Chaque pack donne un accès à vie à la plateforme, plus une maintenance mensuelle ou annuelle pour garder votre banque en ligne.",
      recommended: "Recommandé", pack: "Pack",
      priceLabel: "Prix d'accès unique",
      maintenance: "Maintenance :", or: "ou",
      coinsMonth: "Coins/mois", coinsYear: "Coins/an",
      users: "utilisateurs", domains: "domaine(s)",
      extraUser: "Coins / utilisateur supplémentaire",
      choosePlan: "Choisir",
      note: "Vous pouvez ajouter des utilisateurs supplémentaires ou passer au pack supérieur à tout moment.",
    },
    domains: {
      label: "Marketplace domaines",
      h2a: "Achetez votre domaine", h2b: "sans quitter la plateforme.",
      sub: "Cherchez, achetez et connectez un nom de domaine directement à votre banque.",
      badge: "DNS et SSL auto-configurés",
      previewLabel: "Aperçu marketplace",
      available: "Disponible", unavailable: "Indisponible",
    },
    cta: {
      h2: "Prêt à devenir un opérateur bancaire ?",
      sub: "Inscrivez-vous, choisissez un plan, validez votre paiement et lancez votre banque dès maintenant.",
      primary: "Créer mon compte", secondary: "J'ai déjà un compte",
    },
    footer: { product: "Produit", pricing: "Tarifs", templates: "Templates", rights: "Tous droits réservés." },
    stats: [
      { v: "4+", l: "Templates actifs" },
      { v: "100%", l: "White-label" },
      { v: "∞", l: "Clients possibles" },
      { v: "24/7", l: "Disponibilité" },
    ],
  },
  en: {
    nav: {
      product: "Product", templates: "Templates", pricing: "Pricing",
      domains: "Domains", login: "Login", cta: "Launch my bank",
    },
    hero: {
      badge: "Multi-Tenant SaaS · Bank-in-a-Box",
      h1a: "Launch your", h1b: "online bank", h1c: "in minutes.",
      sub: "A complete white-label platform for entrepreneurs and fintechs. Pick a template, customize your brand, connect your domain — and go live.",
      ctaPrimary: "Get started free", ctaSecondary: "See pricing",
      trust1: "4 premium templates", trust2: "Auto DNS & SSL", trust3: "JWT + bcrypt secured",
      balanceLabel: "Current balance", balanceNote: "Preview — Tech Neon Template",
    },
    features: {
      label: "Platform",
      h2: "Everything you need to become a banking operator.",
      sub: "Infrastructure, security, UX — all already built. You bring your brand.",
      items: [
        { title: "Unlimited banks", text: "One subscription = one bank. Multiply projects, stay in control." },
        { title: "Premium templates", text: "4 distinctive designs — from Swiss minimal to neon fintech." },
        { title: "Wallet & virtual cards", text: "Virtual cards, transactions, balances — already wired." },
        { title: "Auto subdomain", text: "A subdomain is assigned at creation. Buy your custom domain from the platform." },
        { title: "Banking security", text: "JWT, bcrypt hash, audit logs. Isolated multi-tenant architecture." },
        { title: "Full white-label", text: "Logo, colors, currency, language. Your brand, your bank." },
      ],
    },
    templates: {
      label: "Catalog",
      h2: "More than 4 bank universes.", h2b: "Infinite brands.",
      sub: "Each template is a complete design system — from dashboard to virtual card.",
      cardH3: "A premium client experience.",
      cardSub: "Dashboard, transfers, cards, deposits, credits — all already designed. Change the colors and logo, your clients see your bank.",
      cardCta: "See demo",
    },
    pricing: {
      label: "Pricing",
      h2a: "One pack. One bank.", h2b: "Paid once.",
      sub: "Each pack gives lifetime access to the platform, plus monthly or annual maintenance to keep your bank online.",
      recommended: "Recommended", pack: "Pack",
      priceLabel: "One-time access price",
      maintenance: "Maintenance:", or: "or",
      coinsMonth: "Coins/mo", coinsYear: "Coins/yr",
      users: "users", domains: "domain(s)",
      extraUser: "Coins / extra user",
      choosePlan: "Choose",
      note: "You can add extra users or upgrade your pack at any time.",
    },
    domains: {
      label: "Domain marketplace",
      h2a: "Buy your domain", h2b: "without leaving the platform.",
      sub: "Search, buy and connect a domain name directly to your bank.",
      badge: "DNS & SSL auto-configured",
      previewLabel: "Marketplace preview",
      available: "Available", unavailable: "Unavailable",
    },
    cta: {
      h2: "Ready to become a banking operator?",
      sub: "Sign up, choose a plan, validate your payment with the Super Admin and launch your bank now.",
      primary: "Create my account", secondary: "I already have an account",
    },
    footer: { product: "Product", pricing: "Pricing", templates: "Templates", rights: "All rights reserved." },
    stats: [
      { v: "4+", l: "Active templates" },
      { v: "100%", l: "White-label" },
      { v: "∞", l: "Possible clients" },
      { v: "24/7", l: "Uptime" },
    ],
  },
};

/* ─── Helper components ───────────────────────────────────────────────────── */
function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide"
      style={{ background: "rgba(59,130,246,.10)", color: C.blue, border: `1px solid rgba(59,130,246,.2)` }}>
      {children}
    </span>
  );
}

function GradBtn({ children, to, href, className = "", size = "md", onClick, target }) {
  const cls = `inline-flex items-center gap-2 font-semibold rounded-full transition-all hover:opacity-90 hover:-translate-y-0.5 ${
    size === "lg" ? "px-7 py-3.5 text-base" : "px-5 py-2.5 text-sm"
  } text-white shadow-lg ${className}`;
  const style = { background: grad, boxShadow: "0 8px 24px -8px rgba(59,130,246,.5)" };
  if (to) return <Link to={to} className={cls} style={style} onClick={onClick}>{children}</Link>;
  if (href) return <a href={href} className={cls} style={style} onClick={onClick} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined}>{children}</a>;
  return <button onClick={onClick} className={cls} style={style}>{children}</button>;
}

function OutlineBtn({ children, to, href, className = "", size = "md" }) {
  const cls = `inline-flex items-center gap-2 font-semibold rounded-full border transition-all hover:-translate-y-0.5 ${
    size === "lg" ? "px-7 py-3.5 text-base" : "px-5 py-2.5 text-sm"
  } ${className}`;
  if (to) return <Link to={to} className={cls} style={{ borderColor: C.border, color: C.text }}>{children}</Link>;
  if (href) return <a href={href} className={cls} style={{ borderColor: C.border, color: C.text }}>{children}</a>;
  return <button className={cls} style={{ borderColor: C.border, color: C.text }}>{children}</button>;
}

function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 mb-4">
      <span className="w-6 h-px" style={{ background: grad }} />
      <span className="text-xs font-bold tracking-[0.15em] uppercase" style={{ color: C.blue }}>{children}</span>
    </div>
  );
}

/* ─── Main Landing ───────────────────────────────────────────────────────── */
export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [mobileNav, setMobileNav] = useState(false);
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("neobk_landing_lang") || "fr"; } catch (_) { return "fr"; }
  });
  const T = TRANSLATIONS[lang] || TRANSLATIONS.fr;

  const chooseLang = (l) => {
    setLang(l);
    try { localStorage.setItem("neobk_landing_lang", l); } catch (_) {}
  };

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem("neobk_landing_lang"); } catch (_) {}
    if (saved) return;
    api.get("/public/geo").then((r) => { if (r.data?.lang) setLang(r.data.lang); }).catch(() => {});
  }, []);

  return (
    <>
      <div data-testid="landing-root"
        style={{ fontFamily: "Inter, system-ui, sans-serif", background: C.bg, color: C.text, overflowX: "hidden" }}>

        {/* ── NAV ─────────────────────────────────────────────────────────── */}
        <header style={{ background: "rgba(244,246,255,.85)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 40 }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-extrabold text-sm"
                style={{ background: grad, boxShadow: "0 4px 12px -4px rgba(59,130,246,.6)" }}>N</div>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.15rem", letterSpacing: "-0.02em", color: C.text }}>
                Neobk<span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.</span>Pro
              </span>
            </Link>

            {/* Desktop nav links */}
            <nav className="hidden md:flex items-center gap-7 text-sm font-medium" style={{ color: C.muted }}>
              <a href="#produit" className="hover:opacity-70 transition-opacity">{T.nav.product}</a>
              <a href="#templates" className="hover:opacity-70 transition-opacity">{T.nav.templates}</a>
              <a href="#pricing" className="hover:opacity-70 transition-opacity">{T.nav.pricing}</a>
              <a href="#domaines" className="hover:opacity-70 transition-opacity">{T.nav.domains}</a>
            </nav>

            <div className="flex items-center gap-2">
              {/* Lang toggle — desktop only */}
              <div className="hidden md:flex items-center rounded-full overflow-hidden text-xs font-bold border"
                style={{ borderColor: C.border }}>
                <button onClick={() => chooseLang("fr")}
                  className="px-3 py-1.5 transition-colors"
                  style={lang === "fr" ? { background: grad, color: "#fff" } : { color: C.muted }}>FR</button>
                <button onClick={() => chooseLang("en")}
                  className="px-3 py-1.5 transition-colors"
                  style={lang === "en" ? { background: grad, color: "#fff" } : { color: C.muted }}>EN</button>
              </div>
              {/* Login + CTA — desktop only */}
              <Link to="/login" className="hidden md:block text-sm font-medium hover:opacity-70 transition-opacity px-3" style={{ color: C.muted }} data-testid="nav-login">{T.nav.login}</Link>
              <div className="hidden md:block">
                <GradBtn to="/register" data-testid="nav-cta">{T.nav.cta} <ArrowUpRight className="w-4 h-4" /></GradBtn>
              </div>
              {/* Hamburger — mobile only */}
              <button className="md:hidden p-2 rounded-lg transition-colors" style={{ color: C.muted }}
                onClick={() => setMobileNav(true)} aria-label="Menu">
                <Menu size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile nav — plein écran blanc opaque (rendu HORS du header pour éviter
            le piège du containing-block créé par backdrop-filter) */}
        {mobileNav && (
          <div className="flex flex-col md:hidden"
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: "#ffffff", height: "100dvh" }}>

            {/* Header */}
            <div className="flex items-center justify-between px-5 shrink-0"
              style={{ height: "64px", borderBottom: `1px solid ${C.border}` }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-extrabold text-sm"
                  style={{ background: grad }}>N</div>
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.1rem", color: C.text }}>
                  Neobk<span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.</span>Pro
                </span>
              </div>
              <button onClick={() => setMobileNav(false)} className="p-2 rounded-xl"
                style={{ background: "rgba(59,130,246,.08)", color: C.blue }}>
                <X size={22} />
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex-1 overflow-y-auto px-5 py-6 flex flex-col gap-2">
              {[["#produit", T.nav.product], ["#templates", T.nav.templates], ["#pricing", T.nav.pricing], ["#domaines", T.nav.domains]].map(([href, label]) => (
                <a key={href} href={href}
                  onClick={() => setMobileNav(false)}
                  className="flex items-center justify-between px-5 py-4 rounded-2xl text-base font-semibold"
                  style={{ color: C.text, background: "rgba(59,130,246,.04)", border: `1px solid ${C.border}` }}>
                  {label}
                  <ChevronRight size={18} style={{ color: C.blue }} />
                </a>
              ))}
            </nav>

            {/* Footer — lang + CTA */}
            <div className="px-5 pb-8 pt-4 flex flex-col gap-3 shrink-0"
              style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="flex gap-2">
                <button onClick={() => chooseLang("fr")}
                  className="flex-1 py-2.5 rounded-full text-sm font-bold border transition"
                  style={lang === "fr" ? { background: grad, color: "#fff", borderColor: "transparent" } : { borderColor: C.border, color: C.muted }}>
                  🇫🇷 Français
                </button>
                <button onClick={() => chooseLang("en")}
                  className="flex-1 py-2.5 rounded-full text-sm font-bold border transition"
                  style={lang === "en" ? { background: grad, color: "#fff", borderColor: "transparent" } : { borderColor: C.border, color: C.muted }}>
                  🇬🇧 English
                </button>
              </div>
              <Link to="/login" onClick={() => setMobileNav(false)}
                className="text-center py-3.5 text-sm font-semibold rounded-full border block"
                style={{ borderColor: C.border, color: C.text }}>
                {T.nav.login}
              </Link>
              <Link to="/register" onClick={() => setMobileNav(false)}
                className="text-center py-3.5 text-base font-bold rounded-full text-white block"
                style={{ background: grad, boxShadow: "0 8px 20px -8px rgba(59,130,246,.5)" }}>
                {T.nav.cta} →
              </Link>
            </div>
          </div>
        )}

        {/* ── HERO ────────────────────────────────────────────────────────── */}
        <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-28 md:pt-28 md:pb-36">
          {/* Background blobs */}
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-[120px] pointer-events-none"
            style={{ background: "rgba(139,92,246,.12)" }} />
          <div className="absolute -bottom-20 -left-40 w-[500px] h-[500px] rounded-full blur-[100px] pointer-events-none"
            style={{ background: "rgba(59,130,246,.10)" }} />

          <div className="relative grid lg:grid-cols-12 gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-6 xl:col-span-7">
              <div className="mb-6">
                <Badge><Star className="w-3 h-3" /> {T.hero.badge}</Badge>
              </div>
              <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(2.4rem, 6vw, 4rem)", lineHeight: 1.1, letterSpacing: "-0.03em", color: C.text }}>
                {T.hero.h1a}{" "}
                <span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  {T.hero.h1b}
                </span>
                <br />{T.hero.h1c}
              </h1>
              <p className="mt-6 text-lg leading-relaxed max-w-xl" style={{ color: C.muted }}>{T.hero.sub}</p>

              <div className="mt-10 flex flex-wrap gap-3">
                <GradBtn to="/register" size="lg" data-testid="hero-cta-primary">
                  {T.hero.ctaPrimary} <ArrowUpRight className="w-5 h-5" />
                </GradBtn>
                <OutlineBtn href="#pricing" size="lg" data-testid="hero-cta-secondary">
                  {T.hero.ctaSecondary}
                </OutlineBtn>
              </div>

              <div className="mt-10 flex flex-wrap gap-5 text-sm font-medium" style={{ color: C.muted }}>
                {[
                  [ShieldCheck, T.hero.trust3],
                  [Layers, T.hero.trust1],
                  [Globe2, T.hero.trust2],
                ].map(([Icon, label], i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Icon className="w-4 h-4 shrink-0" style={{ color: C.blue }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: phone preview */}
            <div className="lg:col-span-6 xl:col-span-5 flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow behind card */}
                <div className="absolute inset-10 blur-3xl rounded-full"
                  style={{ background: "rgba(59,130,246,.2)" }} />
                {/* Phone shell */}
                <div className="relative w-[260px] sm:w-[300px] aspect-[9/19] rounded-[2.5rem] border-[6px] p-1 shadow-2xl"
                  style={{ borderColor: C.border, background: C.card }}>
                  <div className="w-full h-full rounded-[2rem] overflow-hidden flex flex-col" style={{ background: C.bg }}>
                    {/* Status bar */}
                    <div className="flex justify-between items-center px-5 pt-4 pb-2 text-[9px] opacity-40" style={{ color: C.text }}>
                      <span>9:41</span><span>●●● ▰▰▰</span>
                    </div>
                    {/* Bank header */}
                    <div className="px-4 pb-2 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-[9px] font-extrabold"
                          style={{ background: grad }}>T</div>
                        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "11px", color: C.text }}>TRUSTBANK</span>
                      </div>
                      <div className="w-6 h-6 rounded-full border" style={{ borderColor: C.border }} />
                    </div>
                    {/* Card */}
                    <div className="px-4 mt-1">
                      <div className="rounded-2xl p-4 aspect-[1.6/1] flex flex-col justify-between text-white shadow-lg"
                        style={{ background: grad }}>
                        <div className="flex justify-between items-start">
                          <div className="text-[7px] tracking-widest uppercase opacity-70">Solde</div>
                          <div className="w-6 h-4 rounded-sm bg-yellow-300/80" />
                        </div>
                        <div>
                          <div className="font-mono text-base font-extrabold">5 280,50 €</div>
                          <div className="text-[7px] opacity-60 mt-0.5">EUR</div>
                        </div>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="px-4 mt-3 grid grid-cols-4 gap-2">
                      {["Send", "Top", "Pay", "More"].map((a) => (
                        <div key={a} className="flex flex-col items-center gap-1 py-2 rounded-xl border" style={{ borderColor: C.border, background: C.card }}>
                          <div className="w-3 h-3 rounded-full" style={{ background: grad }} />
                          <span className="text-[7px] font-medium" style={{ color: C.muted }}>{a}</span>
                        </div>
                      ))}
                    </div>
                    {/* Transactions */}
                    <div className="px-4 mt-3 space-y-2">
                      <div className="text-[8px] font-bold uppercase tracking-widest mb-1 opacity-50" style={{ color: C.muted }}>Récent</div>
                      {[
                        { l: "Salaire", v: "+500 000 €", up: true },
                        { l: "Wassa", v: "-12 500 €", up: false },
                        { l: "Loyer", v: "-45 000 €", up: false },
                      ].map((tx, k) => (
                        <div key={k} className="flex items-center justify-between p-2 rounded-xl" style={{ background: C.card }}>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ background: tx.up ? "#10b98120" : "#ef444420" }}>
                              <span className="text-[7px]" style={{ color: tx.up ? "#10b981" : "#ef4444" }}>{tx.up ? "↓" : "↑"}</span>
                            </div>
                            <span className="text-[8px] font-semibold" style={{ color: C.text }}>{tx.l}</span>
                          </div>
                          <span className="font-mono text-[8px] font-bold" style={{ color: tx.up ? "#10b981" : "#ef4444" }}>{tx.v}</span>
                        </div>
                      ))}
                    </div>
                    {/* Bottom nav */}
                    <div className="mt-auto px-4 py-3 flex justify-around border-t" style={{ borderColor: C.border }}>
                      {["Home", "Cards", "Send", "Me"].map((n, k) => (
                        <div key={n} className="flex flex-col items-center gap-1">
                          <div className="w-3 h-3 rounded-sm" style={{ background: k === 0 ? C.blue : C.border }} />
                          <span className="text-[7px]" style={{ color: k === 0 ? C.blue : C.muted, fontWeight: k === 0 ? 700 : 400 }}>{n}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
            {T.stats.map((s, i) => (
              <div key={i} className="rounded-2xl p-5 text-center border"
                style={{ background: C.card, borderColor: C.border, boxShadow: "0 2px 12px rgba(0,0,0,.04)" }}>
                <div className="font-mono text-3xl font-extrabold"
                  style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Space Grotesk', sans-serif" }}>
                  {s.v}
                </div>
                <div className="text-xs font-medium mt-1" style={{ color: C.muted }}>{s.l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── FEATURES ────────────────────────────────────────────────────── */}
        <section id="produit" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <SectionLabel>{T.features.label}</SectionLabel>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.8rem, 4vw, 2.8rem)", letterSpacing: "-0.02em", color: C.text }}>
              {T.features.h2}
            </h2>
            <p className="mt-4 text-base leading-relaxed" style={{ color: C.muted }}>{T.features.sub}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[Building2, Layers, Wallet, Globe2, ShieldCheck, Sparkles].map((Icon, i) => (
              <div key={i} className="group rounded-2xl p-6 border transition-all hover:-translate-y-1"
                style={{ background: C.card, borderColor: C.border, boxShadow: "0 2px 12px rgba(0,0,0,.04)" }}
                data-testid={`feature-${i}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: "rgba(59,130,246,.08)", border: "1px solid rgba(59,130,246,.15)" }}>
                  <Icon className="w-5 h-5" style={{ color: C.blue }} />
                </div>
                <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "1.05rem", color: C.text, marginBottom: "0.5rem" }}>
                  {T.features.items[i].title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.muted }}>{T.features.items[i].text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── TEMPLATES ───────────────────────────────────────────────────── */}
        <section id="templates" className="py-24" style={{ background: C.card }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <SectionLabel>{T.templates.label}</SectionLabel>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.8rem, 4vw, 2.8rem)", letterSpacing: "-0.02em", color: C.text }}>
                {T.templates.h2}{" "}
                <span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{T.templates.h2b}</span>
              </h2>
              <p className="mt-4 text-base" style={{ color: C.muted }}>{T.templates.sub}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 items-center" data-testid="template-preview">
              {/* Phone mockup — larger */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-6 blur-3xl rounded-full" style={{ background: "rgba(59,130,246,.15)" }} />
                  <div className="relative w-[240px] sm:w-[280px] aspect-[9/19] rounded-[2.5rem] border-[6px] p-1 shadow-2xl"
                    style={{ borderColor: C.border, background: C.card }}>
                    <div className="w-full h-full rounded-[2rem] overflow-hidden flex flex-col" style={{ background: C.bg }}>
                      <div className="flex justify-between items-center px-5 pt-4 pb-2 text-[9px] opacity-40" style={{ color: C.text }}>
                        <span>9:41</span><span>●●● ▰▰▰</span>
                      </div>
                      <div className="px-4 pb-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-[9px] font-extrabold"
                            style={{ background: grad }}>T</div>
                          <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: "11px", color: C.text }}>TRUSTBANK</span>
                        </div>
                        <div className="w-6 h-6 rounded-full border" style={{ borderColor: C.border }} />
                      </div>
                      <div className="px-4 mt-1">
                        <div className="rounded-2xl p-4 aspect-[1.6/1] flex flex-col justify-between text-white shadow-lg" style={{ background: grad }}>
                          <div className="flex justify-between items-start">
                            <div className="text-[7px] tracking-widest uppercase opacity-70">Solde</div>
                            <div className="w-6 h-4 rounded-sm bg-yellow-300/80" />
                          </div>
                          <div>
                            <div className="font-mono text-base font-extrabold">5 280,50 €</div>
                            <div className="text-[7px] opacity-60 mt-0.5">EUR</div>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 mt-3 grid grid-cols-4 gap-2">
                        {["Send", "Top", "Pay", "More"].map((a) => (
                          <div key={a} className="flex flex-col items-center gap-1 py-2 rounded-xl border"
                            style={{ borderColor: C.border, background: C.card }}>
                            <div className="w-3 h-3 rounded-full" style={{ background: grad }} />
                            <span className="text-[7px] font-medium" style={{ color: C.muted }}>{a}</span>
                          </div>
                        ))}
                      </div>
                      <div className="px-4 mt-3 space-y-2 flex-1">
                        {[{ l: "Salaire", v: "+500 000 €", up: true }, { l: "Wassa", v: "-12 500 €", up: false }, { l: "Loyer", v: "-45 000 €", up: false }].map((tx, k) => (
                          <div key={k} className="flex items-center justify-between p-2 rounded-xl" style={{ background: "rgba(244,246,255,.9)" }}>
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded-full flex items-center justify-center"
                                style={{ background: tx.up ? "#10b98120" : "#ef444420" }}>
                                <span className="text-[7px]" style={{ color: tx.up ? "#10b981" : "#ef4444" }}>{tx.up ? "↓" : "↑"}</span>
                              </div>
                              <span className="text-[8px] font-semibold" style={{ color: C.text }}>{tx.l}</span>
                            </div>
                            <span className="font-mono text-[8px] font-bold" style={{ color: tx.up ? "#10b981" : "#ef4444" }}>{tx.v}</span>
                          </div>
                        ))}
                      </div>
                      <div className="px-4 py-3 mt-2 flex justify-around border-t" style={{ borderColor: C.border }}>
                        {["Home", "Cards", "Send", "Me"].map((n, k) => (
                          <div key={n} className="flex flex-col items-center gap-1">
                            <div className="w-3 h-3 rounded-sm" style={{ background: k === 0 ? C.blue : C.border }} />
                            <span className="text-[7px]" style={{ color: k === 0 ? C.blue : C.muted, fontWeight: k === 0 ? 700 : 400 }}>{n}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: text */}
              <div>
                <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.5rem, 3vw, 2.2rem)", letterSpacing: "-0.02em", color: C.text, marginBottom: "1rem" }}>
                  {T.templates.cardH3}
                </h3>
                <p className="text-base leading-relaxed mb-8" style={{ color: C.muted }}>{T.templates.cardSub}</p>
                <div className="space-y-3 mb-8">
                  {["Dashboard & virements", "Cartes virtuelles", "Dépôts & crédits", "Multi-langue & multi-devise"].map((f) => (
                    <div key={f} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: "rgba(59,130,246,.1)" }}>
                        <Check className="w-3 h-3" style={{ color: C.blue }} />
                      </div>
                      <span className="text-sm font-medium" style={{ color: C.text }}>{f}</span>
                    </div>
                  ))}
                </div>
                <GradBtn href="https://icz.finance" target="_blank" size="lg" data-testid="templates-demo-cta">
                  {T.templates.cardCta} <ArrowUpRight className="w-5 h-5" />
                </GradBtn>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRICING ─────────────────────────────────────────────────────── */}
        <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <SectionLabel>{T.pricing.label}</SectionLabel>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.8rem, 4vw, 2.8rem)", letterSpacing: "-0.02em", color: C.text }}>
              {T.pricing.h2a}{" "}
              <span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{T.pricing.h2b}</span>
            </h2>
            <p className="mt-4 text-base" style={{ color: C.muted }}>{T.pricing.sub}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(plans.length ? plans : []).map((p, i) => {
              const featured = !!p.is_featured;
              return (
                <div key={p.id || p.name} className="relative flex flex-col rounded-3xl p-7 border transition-all hover:-translate-y-1"
                  style={featured
                    ? { background: grad, color: "#fff", border: "none", boxShadow: "0 20px 60px -20px rgba(59,130,246,.5)" }
                    : { background: C.card, borderColor: C.border, boxShadow: "0 2px 12px rgba(0,0,0,.04)" }}
                  data-testid={`plan-card-${p.name?.toLowerCase()}`}>

                  {featured && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full whitespace-nowrap shadow"
                      style={{ color: C.blue }}>
                      ⭐ {T.pricing.recommended}
                    </div>
                  )}

                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold mb-1 opacity-60">{T.pricing.pack}</div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.8rem", letterSpacing: "-0.02em" }}>{p.name}</div>
                  <p className="text-sm mt-1 opacity-70">{p.description}</p>

                  <div className="mt-6 rounded-2xl p-4" style={{ background: featured ? "rgba(255,255,255,.15)" : "rgba(59,130,246,.05)", border: featured ? "1px solid rgba(255,255,255,.2)" : `1px solid ${C.border}` }}>
                    <div className="text-[10px] tracking-widest uppercase mb-1 opacity-60">{T.pricing.priceLabel}</div>
                    <div className="flex items-baseline gap-2">
                      <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "2.2rem" }}>
                        {new Intl.NumberFormat("fr-FR").format(p.price)}
                      </span>
                      <span className="text-sm font-bold opacity-70">Coins</span>
                    </div>
                    <div className="text-xs mt-2 flex flex-col gap-0.5 opacity-70">
                      <span>{T.pricing.maintenance} <strong className="opacity-100">{new Intl.NumberFormat("fr-FR").format(p.maintenance_monthly || 2500)} {T.pricing.coinsMonth}</strong></span>
                      <span>{T.pricing.or} <strong className="opacity-100">{new Intl.NumberFormat("fr-FR").format(p.maintenance_annual || 30000)} {T.pricing.coinsYear}</strong></span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                    {[
                      [p.max_users, T.pricing.users],
                      [p.max_domains || 1, T.pricing.domains],
                    ].map(([val, lbl], k) => (
                      <div key={k} className="rounded-2xl py-3 px-2"
                        style={{ background: featured ? "rgba(255,255,255,.15)" : "rgba(59,130,246,.05)" }}>
                        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.5rem" }}>{val}</div>
                        <div className="text-[10px] uppercase tracking-wider mt-0.5 opacity-60">{lbl}</div>
                      </div>
                    ))}
                  </div>

                  <ul className="mt-5 space-y-2.5 text-sm flex-1">
                    {(p.features || []).slice(0, 6).map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 mt-0.5 shrink-0 rounded-full flex items-center justify-center"
                          style={{ background: featured ? "rgba(255,255,255,.25)" : "rgba(59,130,246,.1)" }}>
                          <Check className="w-2.5 h-2.5" style={{ color: featured ? "#fff" : C.blue }} />
                        </div>
                        <span className="opacity-85">{f}</span>
                      </li>
                    ))}
                    <li className="flex items-start gap-2.5">
                      <div className="w-4 h-4 mt-0.5 shrink-0 rounded-full flex items-center justify-center"
                        style={{ background: featured ? "rgba(255,255,255,.25)" : "rgba(59,130,246,.1)" }}>
                        <Check className="w-2.5 h-2.5" style={{ color: featured ? "#fff" : C.blue }} />
                      </div>
                      <span className="text-xs opacity-60">+{new Intl.NumberFormat("fr-FR").format(p.extra_user_price || 3000)} {T.pricing.extraUser}</span>
                    </li>
                  </ul>

                  <Link to="/register" className="mt-8 block" data-testid={`plan-cta-${p.name?.toLowerCase()}`}>
                    <button className="w-full rounded-2xl py-3.5 font-bold text-sm transition"
                      style={featured
                        ? { background: "#fff", color: C.blue }
                        : { background: grad, color: "#fff", boxShadow: "0 8px 20px -8px rgba(59,130,246,.5)" }}>
                      {T.pricing.choosePlan} {p.name} →
                    </button>
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs mt-8" style={{ color: C.muted }}>{T.pricing.note}</p>
        </section>

        {/* ── DOMAINS ─────────────────────────────────────────────────────── */}
        <section id="domaines" className="py-24" style={{ background: C.card }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <SectionLabel>{T.domains.label}</SectionLabel>
                <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.8rem, 4vw, 2.8rem)", letterSpacing: "-0.02em", color: C.text }}>
                  {T.domains.h2a}<br />
                  <span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{T.domains.h2b}</span>
                </h2>
                <p className="mt-5 text-base leading-relaxed" style={{ color: C.muted }}>
                  {T.domains.sub}
                  <span className="font-semibold ml-1" style={{ color: C.text }}>.com, .io, .ci, .ng, .fr</span>
                </p>
                <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full border"
                  style={{ background: "rgba(59,130,246,.05)", borderColor: "rgba(59,130,246,.2)" }}>
                  <Zap className="w-4 h-4" style={{ color: C.blue }} />
                  <span className="text-sm font-medium" style={{ color: C.text }}>{T.domains.badge}</span>
                </div>
              </div>

              <div className="rounded-3xl p-7 border" style={{ background: C.bg, borderColor: C.border }}>
                <div className="text-xs font-bold tracking-widest uppercase mb-5" style={{ color: C.muted }}>{T.domains.previewLabel}</div>
                <div className="space-y-3">
                  {[
                    { d: "mafinance.com", price: "15 000 Coins", available: true },
                    { d: "mafinance.io", price: "35 000 Coins", available: true },
                    { d: "mafinance.ng", price: "18 000 Coins", available: false },
                    { d: "mafinance.ci", price: "12 000 Coins", available: true },
                  ].map((r) => (
                    <div key={r.d} className="flex items-center justify-between p-4 rounded-2xl border transition-all"
                      style={{ background: C.card, borderColor: r.available ? "rgba(59,130,246,.15)" : C.border }}>
                      <div>
                        <div className="font-mono text-sm font-semibold" style={{ color: C.text }}>{r.d}</div>
                        <div className="text-xs mt-0.5 font-medium" style={{ color: r.available ? C.blue : C.muted }}>
                          {r.available ? T.domains.available : T.domains.unavailable}
                        </div>
                      </div>
                      <div className="text-sm font-bold" style={{ color: r.available ? C.purple : C.muted }}>{r.price}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA BAND ────────────────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="relative rounded-3xl p-12 md:p-20 overflow-hidden text-white"
            style={{ background: grad, boxShadow: "0 30px 80px -30px rgba(59,130,246,.5)" }}>
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full blur-3xl" style={{ background: "rgba(255,255,255,.12)" }} />
            <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl" style={{ background: "rgba(139,92,246,.3)" }} />
            <div className="relative">
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.8rem, 5vw, 3.5rem)", letterSpacing: "-0.02em", maxWidth: "36rem" }}>
                {T.cta.h2}
              </h2>
              <p className="mt-5 max-w-xl text-white/80 text-base leading-relaxed">{T.cta.sub}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/register" data-testid="footer-cta-primary"
                  className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold rounded-full transition hover:-translate-y-0.5"
                  style={{ background: "#fff", color: C.blue, boxShadow: "0 8px 24px -8px rgba(0,0,0,.2)" }}>
                  {T.cta.primary} <ArrowUpRight className="w-5 h-5" />
                </Link>
                <Link to="/login" data-testid="footer-cta-secondary"
                  className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold rounded-full border border-white/30 text-white transition hover:-translate-y-0.5 hover:bg-white/10">
                  {T.cta.secondary}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── FOOTER ──────────────────────────────────────────────────────── */}
        <footer className="border-t py-10" style={{ borderColor: C.border }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-extrabold text-xs"
                style={{ background: grad }}>N</div>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, color: C.text }}>Neobk.Pro</span>
              <span className="text-sm ml-2" style={{ color: C.muted }}>© 2026 · {T.footer.rights}</span>
            </div>
            <div className="flex gap-6 text-sm font-medium" style={{ color: C.muted }}>
              <a href="#produit" className="hover:opacity-70 transition-opacity">{T.footer.product}</a>
              <a href="#pricing" className="hover:opacity-70 transition-opacity">{T.footer.pricing}</a>
              <a href="#templates" className="hover:opacity-70 transition-opacity">{T.footer.templates}</a>
            </div>
          </div>
        </footer>
      </div>

      <ChatWidget />
    </>
  );
}

/* ─── Visitor Chat Widget ─────────────────────────────────────────────────── */
function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [proactive, setProactive] = useState(false);
  const msgsEndRef = useRef(null);
  const pollRef = useRef(null);
  const inboxRef = useRef(null);
  const pingRef = useRef(null);
  const sessionIdRef = useRef(null);
  const convIdRef = useRef(null);
  const fileInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    const initSession = async () => {
      const stored = localStorage.getItem("neobank_visitor_session");
      try {
        const { data } = await api.post("/visitor/session", {
          session_id: stored || undefined,
          page: window.location.pathname,
          referrer: document.referrer || undefined,
          user_agent: navigator.userAgent,
        });
        const sid = data.session_id;
        localStorage.setItem("neobank_visitor_session", sid);
        setSessionId(sid);
        sessionIdRef.current = sid;
        if (data.conv_id) {
          setConvId(data.conv_id);
          convIdRef.current = data.conv_id;
          setProactive(true);
          setStep("chat");
        }
      } catch {}
    };
    initSession();
    pingRef.current = setInterval(async () => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      try { await api.post("/visitor/session", { session_id: sid, page: window.location.pathname }); } catch {}
    }, 10000);
    return () => clearInterval(pingRef.current);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const checkInbox = async () => {
      try {
        const { data } = await api.get(`/visitor/${sessionId}/inbox`);
        if (data.conv_id && data.messages?.length > 0) {
          setConvId(data.conv_id);
          convIdRef.current = data.conv_id;
          setProactive(true);
          setStep("chat");
          setMessages(data.messages);
          msgsEndRef.current?.scrollIntoView({ behavior: "smooth" });
          if (!open) {
            setUnread(data.messages.filter((m) => m.sender === "super_admin").length);
          }
        }
      } catch {}
    };
    inboxRef.current = setInterval(checkInbox, 5000);
    return () => clearInterval(inboxRef.current);
  }, [sessionId, open]);

  const loadMessages = async (id) => {
    try {
      const { data } = await api.get(`/chat/${id}/messages`);
      setMessages(data);
      msgsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch {}
  };

  useEffect(() => {
    if (!convId || !open || proactive) return;
    loadMessages(convId);
    pollRef.current = setInterval(() => loadMessages(convId), 3000);
    return () => clearInterval(pollRef.current);
  }, [convId, open, proactive]);

  useEffect(() => {
    if (!open && messages.length > 0) {
      setUnread(messages.filter((m) => m.sender === "super_admin").length);
    }
  }, [open, messages]);

  const startChat = async () => {
    if (!email.trim()) return;
    try {
      const { data } = await api.post("/chat/start", {
        email: email.trim(),
        name: name.trim(),
        session_id: sessionIdRef.current || undefined,
      });
      setConvId(data.conversation_id);
      convIdRef.current = data.conversation_id;
      setStep("chat");
    } catch { setStep("chat"); }
  };

  const sendMsg = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      if (proactive && sessionId) {
        await api.post(`/visitor/${sessionId}/reply`, { content: input });
        setInput("");
        const { data } = await api.get(`/visitor/${sessionId}/inbox`);
        if (data.messages) { setMessages(data.messages); msgsEndRef.current?.scrollIntoView({ behavior: "smooth" }); }
      } else if (convId) {
        await api.post(`/chat/${convId}/messages`, { content: input });
        setInput("");
        await loadMessages(convId);
      }
    } catch {}
    finally { setSending(false); }
  };

  const sendFile = async (file) => {
    if (!convId || !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/chat/${convId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (proactive && sessionId) {
        const { data } = await api.get(`/visitor/${sessionId}/inbox`);
        if (data.messages) setMessages(data.messages);
      } else {
        await loadMessages(convId);
      }
    } catch {}
    finally { setSending(false); fileInputRef.current && (fileInputRef.current.value = ""); }
  };

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  const renderAttachment = (att) => {
    if (!att) return null;
    const isImage = att.mime?.startsWith("image/");
    const url = att.url?.startsWith("http") ? att.url : `${BACKEND_URL}${att.url}`;
    if (isImage) return <img src={url} alt={att.filename} className="max-w-[180px] rounded-lg mt-1 block" />;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 mt-1 text-xs underline opacity-70">
        <span>📎</span>{att.filename}
      </a>
    );
  };

  return (
    <>
      <button
        onClick={() => { setOpen(!open); setUnread(0); }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full text-white flex items-center justify-center shadow-xl transition hover:opacity-90 hover:-translate-y-0.5"
        style={{ background: grad, boxShadow: "0 8px 24px -8px rgba(59,130,246,.6)" }}
        aria-label="Chat support">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{unread}</span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 rounded-2xl shadow-2xl border flex flex-col overflow-hidden"
          style={{ height: "480px", background: C.card, borderColor: C.border }}>
          <div className="p-4 text-white" style={{ background: grad }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>Support Neobk.Pro</div>
            <div className="text-xs opacity-70 mt-0.5">Nous répondons généralement en quelques minutes</div>
          </div>

          {step === "email" && !proactive ? (
            <div className="flex-1 p-4 flex flex-col gap-3 justify-center">
              <p className="text-sm" style={{ color: C.muted }}>Avant de commencer, entrez votre email pour qu'on puisse vous recontacter.</p>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: C.muted }}>Nom (optionnel)</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre prénom"
                  className="w-full rounded-xl px-3 py-2 text-sm outline-none"
                  style={{ border: `1px solid ${C.border}`, color: C.text }} />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: C.muted }}>Email *</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="vous@email.com"
                  className="w-full rounded-xl px-3 py-2 text-sm outline-none"
                  style={{ border: `1px solid ${C.border}`, color: C.text }}
                  onKeyDown={(e) => e.key === "Enter" && startChat()} />
              </div>
              <button onClick={startChat} disabled={!email.trim()}
                className="w-full rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-40 transition"
                style={{ background: grad }}>
                Démarrer la conversation
              </button>
            </div>
          ) : (
            <>
              {proactive && (
                <div className="px-3 pt-2 pb-1">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: "#10b981", background: "#10b98115" }}>Message du support</span>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {!messages.length && (
                  <div className="text-center text-xs mt-8" style={{ color: C.muted }}>Envoyez votre premier message, nous vous répondrons rapidement.</div>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "super_admin" ? "justify-start" : "justify-end"}`}>
                    <div className="max-w-[82%] rounded-2xl px-3 py-2 text-sm"
                      style={m.sender === "super_admin"
                        ? { background: C.bg, color: C.text, borderRadius: "4px 16px 16px 16px" }
                        : { background: grad, color: "#fff", borderRadius: "16px 4px 16px 16px" }}>
                      {m.content && <p className="whitespace-pre-wrap">{m.content}</p>}
                      {m.attachment && renderAttachment(m.attachment)}
                      <div className="text-[10px] mt-0.5 opacity-50">{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                ))}
                <div ref={msgsEndRef} />
              </div>
              <div className="p-2 border-t" style={{ borderColor: C.border }}>
                <div className="flex gap-1.5">
                  <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Écrivez votre message…"
                    className="flex-1 rounded-xl px-3 py-2 text-sm outline-none"
                    style={{ border: `1px solid ${C.border}`, color: C.text }}
                    onKeyDown={(e) => e.key === "Enter" && sendMsg()} disabled={sending} />
                  {convId && (
                    <button onClick={() => fileInputRef.current?.click()} disabled={sending}
                      className="rounded-xl px-2.5 py-2 transition disabled:opacity-40"
                      style={{ border: `1px solid ${C.border}`, color: C.muted }}>
                      <Paperclip size={16} />
                    </button>
                  )}
                  <button onClick={sendMsg} disabled={!input.trim() || sending || (!convId && !proactive)}
                    className="rounded-xl px-3 py-2 text-white disabled:opacity-40 transition"
                    style={{ background: grad }}>
                    <Send size={16} />
                  </button>
                </div>
                <input ref={fileInputRef} type="file" className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.mp3,.wav,.ogg,.txt"
                  onChange={(e) => e.target.files?.[0] && sendFile(e.target.files[0])} />
                <div className="text-[10px] text-center mt-1" style={{ color: C.muted }}>Images, PDF, docs, audio acceptés</div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
